/**
 * Fallback block renderer
 *
 * Lightweight renderers for the basic/legacy block types that the main
 * ComponentRenderers switch does not handle. Kept in its own module (separate
 * from the heavy ComponentRenderers) so it stays in the main chunk and can be
 * used synchronously while the ComponentRenderers chunk loads lazily.
 */

import styled from '@emotion/styled';
import type { FunnelOffer, StepPageBlock } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStringProp(block: StepPageBlock, key: string, fallback: string): string {
  const value = block.props?.[key];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function formatPrice(value: number | null | undefined): string {
  if (!Number.isFinite(value)) return '$0.00';
  return `$${(value as number).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Styled primitives for fallback block rendering
// ---------------------------------------------------------------------------

const BlockCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 14px;
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 24px;
  line-height: 1.2;
`;

const Text = styled.p`
  margin: 0;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Image = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.radius.regular};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OldPrice = styled.span`
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NewPrice = styled.span`
  font-size: 24px;
  font-weight: 700;
`;

// ---------------------------------------------------------------------------
// Fallback block renderer (basic types not handled by ComponentRenderers)
// ---------------------------------------------------------------------------

export function renderFallbackBlock(block: StepPageBlock, offer: FunnelOffer) {
  if (block.type === 'offer_card' || block.type === 'hero') {
    const imgUrl = getStringProp(block, 'imageUrl', offer.imageUrl ?? '');
    const bgColor = getStringProp(block, 'backgroundColor', '');
    const textColor = getStringProp(block, 'textColor', '#ffffff');
    const title = getStringProp(block, 'title', '') || getStringProp(block, 'headline', offer.headline);
    const subtitle = getStringProp(block, 'subtitle', '') || getStringProp(block, 'subheadline', offer.description);
    return (
      <BlockCard key={block.id} style={bgColor ? { background: bgColor, color: textColor, border: 'none' } : undefined}>
        {imgUrl && <Image src={imgUrl} alt={title} />}
        <Title style={bgColor ? { color: textColor } : undefined}>{title}</Title>
        <Text style={bgColor ? { color: textColor, opacity: 0.85 } : undefined}>{subtitle}</Text>
      </BlockCard>
    );
  }

  if (block.type === 'rich_text' || block.type === 'text') {
    return (
      <BlockCard key={block.id}>
        <Text>{getStringProp(block, 'content', offer.description)}</Text>
      </BlockCard>
    );
  }

  if (block.type === 'image' || block.type === 'image-gallery') {
    return (
      <BlockCard key={block.id}>
        {offer.imageUrl ? <Image src={offer.imageUrl} alt={offer.headline} /> : <Text>Product image</Text>}
      </BlockCard>
    );
  }

  if (block.type === 'product-offer') {
    return (
      <BlockCard key={block.id}>
        <Title>{offer.headline}</Title>
        <Text>{offer.description}</Text>
        <PriceRow>
          <OldPrice>{formatPrice(offer.originalPrice)}</OldPrice>
          <NewPrice>{formatPrice(offer.pricingValue)}</NewPrice>
        </PriceRow>
      </BlockCard>
    );
  }

  if (block.type === 'spacer') {
    const height = Math.max(8, Number(block.props?.height ?? 16));
    return <div key={block.id} style={{ height }} aria-hidden="true" />;
  }

  if (block.type === 'divider') {
    return (
      <BlockCard key={block.id}>
        <hr />
      </BlockCard>
    );
  }

  if (block.type === 'banner') {
    const title = getStringProp(block, 'title', offer.headline);
    const bgColor = getStringProp(block, 'backgroundColor', '#f5f5f5');
    return (
      <BlockCard key={block.id} style={{ background: bgColor, border: 'none' }}>
        <Title>{title}</Title>
      </BlockCard>
    );
  }

  if (block.type === 'product-row' || block.type === 'product-card') {
    const name = getStringProp(block, 'name', offer.headline);
    const imgUrl = getStringProp(block, 'imageUrl', offer.imageUrl ?? '');
    const badge = getStringProp(block, 'badge', '');
    const origPrice = getStringProp(block, 'originalPrice', formatPrice(offer.originalPrice));
    const discPrice = getStringProp(block, 'discountedPrice', formatPrice(offer.pricingValue));
    return (
      <BlockCard key={block.id} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        {imgUrl && <Image src={imgUrl} alt={name} style={{ width: '80px', maxHeight: '80px', flex: '0 0 80px' }} />}
        <div style={{ flex: 1 }}>
          <Title style={{ fontSize: '16px', marginBottom: '4px' }}>{name}</Title>
          <PriceRow>
            <OldPrice>{origPrice}</OldPrice>
            <NewPrice style={{ fontSize: '18px' }}>{discPrice}</NewPrice>
            {badge && <span style={{ fontSize: '12px', fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>{badge}</span>}
          </PriceRow>
        </div>
      </BlockCard>
    );
  }

  if (block.type === 'countdown') {
    const label = getStringProp(block, 'label', 'Offer expires in');
    return (
      <BlockCard key={block.id} style={{ textAlign: 'center', padding: '10px' }}>
        <Text style={{ fontWeight: 600 }}>{label}</Text>
      </BlockCard>
    );
  }

  return null;
}
